import { AuthError, resendSignUpCode, signIn } from "aws-amplify/auth";
import { paths } from "config/paths";
import { useFormik } from "formik";
import { useEffect } from "react";
import {
	sendSignInEvent,
	useSendSignInPageViewEvent,
} from "~tag-manager/send-event";
import { useWebRouter } from "~utils/next-router/use-web-router";
import { saveSignInOrigin } from "~utils/functions/get-signout-redirect-path";
import { useAuthContext } from "~auth/contexts/auth";
import {
	getRedirFromQuery,
	normalizeEmail,
	resolveSafeRedirOrFallback,
} from "~auth/helpers";
import { usePageLoaded } from "~auth/hooks/use-page-loaded";
import { getCurrentUserRoutingDecision } from "~auth/queries/get-current-user-routing-decision";
import { toast } from "~ui-components/components/atoms/toaster";
import {
	SignInFormValues,
	SignInSchema,
	SignIn as View,
} from "~ui-components/components/templates/sign-in";

const INVALID_CREDS_ERROR_MESSAGE =
	"Something's not right. Check your email and password and try again.";

export const SignInView = () => {
	useSendSignInPageViewEvent();
	const router = useWebRouter();
	const { refreshSession, signOut } = useAuthContext();
	const isPageLoaded = usePageLoaded();
	useEffect(() => {
		if (router.query.migrated !== "1") return;
		toast.error(
			"Your account has moved to the new MangoMap. Please sign in again to continue.",
		);
		const redir = getRedirFromQuery(router.query);
		void router.replace(
			redir
				? `${paths.signIn}?redir=${encodeURIComponent(redir)}`
				: paths.signIn,
			undefined,
			{ shallow: true },
		);
	}, [router]);
	const form = useFormik<SignInFormValues>({
		validateOnMount: true,
		initialValues: { email: "", password: "" },
		validationSchema: SignInSchema,
		onSubmit: (values, helpers) => {
			toast.promise(
				async () => {
					const email = normalizeEmail(values.email);
					const v3Response = await fetch("/api/auth/sign-in-v3", {
						method: "POST",
						headers: { "content-type": "application/json" },
						body: JSON.stringify({ email, password: values.password }),
					});
					const v3 = (await v3Response.json()) as {
						result: string;
						redirectUrl?: string;
					};

					if (v3.result === "SIGNED_IN_V3") {
						sendSignInEvent(email);
						window.location.href = resolveSafeRedirOrFallback({
							redir: getRedirFromQuery(router.query),
							fallback: v3.redirectUrl ?? "/maps",
						});
						return;
					}

					const signInResult = await signIn({
						username: email,
						password: values.password,
						options: { authFlowType: "USER_PASSWORD_AUTH" },
					});
					if (signInResult.nextStep.signInStep === "CONFIRM_SIGN_UP") {
						await resendSignUpCode({ username: email });
						void router.push(paths.signUpVerificationV4(email));
						return;
					}
					if (signInResult.nextStep.signInStep !== "DONE") throw new Error();

					const routing = await getCurrentUserRoutingDecision();
					if (!routing.allowed) {
						await signOut();
						throw new Error("V4_NOT_RELEASED");
					}

					const { authAccount } = await refreshSession();
					if (!authAccount) throw new Error();
					saveSignInOrigin({ type: "main-domain" });
					sendSignInEvent(email);
					void router.push(
						resolveSafeRedirOrFallback({
							redir: getRedirFromQuery(router.query),
							fallback: paths.accountName.maps.root(authAccount.slug),
							accountSlug: authAccount.slug,
						}),
					);
				},
				{
					loading: "Signing you in...",
					error: (error) => {
						helpers.setSubmitting(false);
						if (error instanceof Error && error.message === "V4_NOT_RELEASED")
							return "Sign-in is temporarily unavailable. Please try again later.";
						if (error instanceof AuthError)
							return "Incorrect username or password.";
						return INVALID_CREDS_ERROR_MESSAGE;
					},
				},
			);
		},
	});

	if (!isPageLoaded) return null;
	return <View form={form} />;
};
